import { GamePhase } from '@susdle/shared';
import type { Snapshot } from '../session/LobbySession.js';
import type { AudioPlayer } from './AudioPlayer.js';
import type { AudioEvent, SoundClass } from './events.js';

/** Phases that get the `phaseChange` event on entry. */
const PHASE_CHANGE_PHASES: ReadonlySet<GamePhase> = new Set([
  GamePhase.ClueGiving,
  GamePhase.Discussion,
  GamePhase.RoleReveal,
  GamePhase.GhostGuessing,
  GamePhase.GuessReveal,
]);

/** Window allowed between a phase's start and a freshness-gated emit. */
const FRESHNESS_MS = 2_000;

interface PhaseTimerView {
  phaseEndsAt: number | null;
  durationMs: number;
}

function getTimerView(snapshot: Snapshot): PhaseTimerView | null {
  switch (snapshot.phase) {
    case GamePhase.ClueGiving:    return snapshot.cluegivingView;
    case GamePhase.Discussion:    return snapshot.discussionView;
    case GamePhase.GhostGuessing: return snapshot.ghostGuessingView;
    // RoleReveal and GuessReveal have no server timer — treated as always fresh.
    default: return null;
  }
}

function isFresh(snapshot: Snapshot): boolean {
  const view = getTimerView(snapshot);
  if (view === null) return true;
  if (view.phaseEndsAt === null) return true;
  const phaseStartedAt = view.phaseEndsAt - view.durationMs;
  const baseNow = snapshot.clockOffset === 0 ? Date.now() : Date.now() + snapshot.clockOffset;
  return baseNow - phaseStartedAt <= FRESHNESS_MS;
}

/**
 * Diffs successive LobbySession snapshots and dispatches audio events
 * via the injected AudioPlayer. Pure logic — no React, no DOM, no Howler.
 *
 * The Reconnect Guard (SOUND.md §5): on construction `armed = false` and
 * `lastSnapshot = null`. The very first snapshot hydrates the baseline
 * silently. ANY later non-`ready` → `ready` `bootPhase` transition also
 * re-disarms — covering both literal reconnects (`'reconnecting' → 'ready'`)
 * AND a mid-game rejoin where the first `ready` snapshot already carries
 * a non-Lobby phase. Both cases would otherwise fire phantom events for
 * a diff against the initial-Lobby boot baseline; the broader check
 * silences both.
 *
 * Tasks 7-12 fill in the actual diff logic. This task wires the skeleton
 * and the guard only.
 */
export class AudioDirector {
  private lastSnapshot: Snapshot | null = null;
  private armed = false;
  private clockRafHandle: number | null = null;
  private lastLowTimeForGiver: string | null = null;
  private reducedMotion = false;
  private lobbyPlaying = false;
  /** Mirror of the Music class volume (pushed via `setVolume`). The player's
   *  play() is a silent no-op at class volume 0 (no howl is ever created),
   *  so the ambient latches below must know it to stay truthful. */
  private musicVolume = 1;
  /** Which end-cadence is currently looping (or null if not in GameEnd).
   *  Reconciled on every snapshot — see `reconcileEndCadence`. */
  private endCadencePlaying: AudioEvent | null = null;

  constructor(private readonly player: AudioPlayer) {}

  setReducedMotion(value: boolean): void {
    const wasOn = this.reducedMotion;
    this.reducedMotion = value;
    if (value && !wasOn) {
      // Flipping ON mid-session — cut any active lobby bed.
      this.stopLobbyBed();
    } else if (!value && wasOn && this.lastSnapshot !== null) {
      // Flipping OFF mid-session — bring the bed back if we're sitting in
      // the Lobby (reconcile is a no-op elsewhere). The bed is a
      // whole-table ambient loop, so starting it on a local preference
      // change leaks nothing (Hot-Mic Rule).
      this.reconcileLobbyBed(this.lastSnapshot);
    }
  }

  onSnapshot(next: Snapshot): void {
    // Reconnect Guard: ANY non-'ready' → 'ready' transition disarms, so
    // the snapshot carrying the freshly-ready state hydrates silently.
    // This covers both reconnects ('reconnecting' → 'ready') AND the
    // first arrival at 'ready' from the boot sequence — the latter
    // matters because a player who rejoins a game already in progress
    // sees their first 'ready' snapshot carry a non-Lobby phase, which
    // would otherwise fire a phantom phaseChange for the diff against
    // the initial Lobby-phase boot snapshot.
    const wasReconnecting =
      this.lastSnapshot !== null &&
      this.lastSnapshot.bootPhase !== 'ready' &&
      next.bootPhase === 'ready';
    if (wasReconnecting) this.armed = false;

    if (!this.armed) {
      this.lastSnapshot = next;
      this.armed = true;
      this.ensureClockLoopState(next);
      // The lobby bed is ambient (not an edge event) — reconcile it
      // against the hydrated phase so initial-arrival and reconnect-into
      // both land in the right state without firing any edge event.
      this.reconcileLobbyBed(next);
      // The end-cadence loops are also ambient (Bookend Rule). Reconnect
      // mid-GameEnd hydrates silently otherwise — reconcile starts the
      // right cadence so the rejoiner hears the same loop as everyone else.
      this.reconcileEndCadence(next);
      return;
    }

    const prev = this.lastSnapshot;
    if (prev === null) {
      // Defensive: armed=true implies prev=non-null in normal flow.
      this.lastSnapshot = next;
      this.ensureClockLoopState(next);
      return;
    }

    this.diff(prev, next);
    this.lastSnapshot = next;
    this.ensureClockLoopState(next);
    this.reconcileEndCadence(next);
  }

  private reconcileLobbyBed(snapshot: Snapshot): void {
    const shouldPlay = snapshot.phase === GamePhase.Lobby && !this.reducedMotion;
    if (shouldPlay) this.playLobbyBed();
    else this.stopLobbyBed();
  }

  private playLobbyBed(): void {
    if (this.lobbyPlaying) return;
    // Don't latch what can't start: at Music volume 0 the player's play()
    // is a no-op, so latching here would leave the bed silently "on" —
    // un-restartable until the next phase edge even after the slider is
    // raised. The 0→up edge in `setVolume` reconciles instead.
    if (this.musicVolume === 0) return;
    this.player.play('lobby');
    this.lobbyPlaying = true;
  }

  private stopLobbyBed(): void {
    if (!this.lobbyPlaying) return;
    this.player.stop('lobby');
    this.lobbyPlaying = false;
  }

  setVolume(soundClass: SoundClass, value: number): void {
    this.player.setVolume(soundClass, value);
    if (soundClass === 'music') {
      const wasMuted = this.musicVolume === 0;
      this.musicVolume = value;
      if (wasMuted && value > 0 && this.lastSnapshot !== null) {
        // Raising Music out of 0 mid-phase — start any ambient loop that
        // was skipped (not latched) while muted. Both beds are whole-table
        // loops (Bookend Rule), so a local volume change starting them
        // leaks nothing (Hot-Mic Rule).
        this.reconcileLobbyBed(this.lastSnapshot);
        this.reconcileEndCadence(this.lastSnapshot);
      }
    }
  }

  dispose(): void {
    this.stopClueClockLoop();
    this.player.dispose();
    this.lastSnapshot = null;
    this.armed = false;
  }

  private diff(prev: Snapshot, next: Snapshot): void {
    if (prev.phase !== next.phase) {
      this.onPhaseChange(prev, next);
    }
    // Same-phase ClueGiving turn change — reset the lowTime dedupe; cut
    // any still-playing lowTime from the prior turn (prevents overlap if
    // the sample outlives the turn); fire yourClueTurn if active is me.
    if (
      prev.phase === GamePhase.ClueGiving &&
      next.phase === GamePhase.ClueGiving &&
      prev.cluegivingView?.currentClueGiverId !== next.cluegivingView?.currentClueGiverId
    ) {
      this.lastLowTimeForGiver = null;
      this.player.stop('lowTime');
      if (this.isMyClueTurn(next)) this.player.play('yourClueTurn');
    }
  }

  /** True iff `next` is ClueGiving AND the local player (`meId`) is the
   *  active clue-giver. Used by both the same-phase turn-pass dispatch
   *  and the phase-entry dispatch. Per Hot-Mic analysis (SOUND.md §5),
   *  clue-giving turn order is not role-revealing — private cue is safe. */
  private isMyClueTurn(next: Snapshot): boolean {
    const view = next.cluegivingView;
    if (view === null) return false;
    if (view.currentClueGiverId === null) return false;
    return view.currentClueGiverId === view.meId;
  }

  /**
   * Reconciles the rAF loop's run-state with the current phase. Called at
   * the END of every onSnapshot (including the hydrate-silently path), so
   * reconnecting mid-ClueGiving still arms the clock — the freshness
   * backstop inside the tick handles stale snapshots.
   */
  private ensureClockLoopState(next: Snapshot): void {
    const isInClue = next.phase === GamePhase.ClueGiving;
    if (isInClue && this.clockRafHandle === null) this.startClueClockLoop();
    if (!isInClue && this.clockRafHandle !== null) this.stopClueClockLoop();
  }

  private startClueClockLoop(): void {
    if (this.clockRafHandle !== null) return;
    this.lastLowTimeForGiver = null;
    const tick = (): void => {
      const snapshot = this.lastSnapshot;
      if (snapshot === null || snapshot.phase !== GamePhase.ClueGiving) {
        this.stopClueClockLoop();
        return;
      }
      const view = snapshot.cluegivingView;
      if (view === null || view.phaseEndsAt === null) {
        this.clockRafHandle = requestAnimationFrame(tick);
        return;
      }
      const giverId = view.currentClueGiverId ?? '';
      // No `isFresh` check here: the per-frame `remaining = phaseEndsAt -
      // serverNow()` calculation IS the freshness signal for lowTime. A
      // stale reconnect into mid-ClueGiving still wants the tick if real
      // time remains. The per-giver dedup prevents multi-fire within a turn.
      if (this.lastLowTimeForGiver !== giverId) {
        const baseNow = snapshot.clockOffset === 0 ? Date.now() : Date.now() + snapshot.clockOffset;
        const remaining = view.phaseEndsAt - baseNow;
        if (remaining > 0 && remaining <= 5_000) {
          this.player.play('lowTime');
          this.lastLowTimeForGiver = giverId;
        }
      }
      this.clockRafHandle = requestAnimationFrame(tick);
    };
    this.clockRafHandle = requestAnimationFrame(tick);
  }

  private stopClueClockLoop(): void {
    if (this.clockRafHandle !== null) {
      cancelAnimationFrame(this.clockRafHandle);
      this.clockRafHandle = null;
    }
    this.lastLowTimeForGiver = null;
  }

  private onPhaseChange(prev: Snapshot, next: Snapshot): void {
    // Leaving Lobby → cut the bed.
    if (prev.phase === GamePhase.Lobby && next.phase !== GamePhase.Lobby) {
      this.stopLobbyBed();
    }

    // Leaving ClueGiving → cut any still-playing lowTime sample so it
    // can't bleed into Discussion / RoleReveal / etc. The doctrine specs
    // a tight ~5s clip but this enforces the bound regardless of length.
    if (prev.phase === GamePhase.ClueGiving && next.phase !== GamePhase.ClueGiving) {
      this.player.stop('lowTime');
    }

    // Entering Lobby → start the bed (suppressed when reducedMotion is on).
    if (next.phase === GamePhase.Lobby && prev.phase !== GamePhase.Lobby) {
      if (!this.reducedMotion) this.playLobbyBed();
    }

    // Entering GhostMeeting → night falls.
    if (next.phase === GamePhase.GhostMeeting && prev.phase !== GamePhase.GhostMeeting) {
      this.player.play('nightFall');
      // Lobby → GhostMeeting is the chapbook opening.
      if (prev.phase === GamePhase.Lobby) {
        this.player.play('enterGame');
      }
    }

    // Entering RoleReveal → the plate flips.
    if (next.phase === GamePhase.RoleReveal && prev.phase !== GamePhase.RoleReveal) {
      this.player.play('roleRevealFlip');
    }

    // Public phase change (the bell), freshness-gated. Excludes GhostMeeting
    // (gets nightFall) and GameEnd (gets a cadence loop — reconciled below).
    if (PHASE_CHANGE_PHASES.has(next.phase) && isFresh(next)) {
      this.player.play('phaseChange');
    }

    // Entering ClueGiving with me as first speaker → yourClueTurn.
    // Same-phase turn passes are handled by `diff`; this covers round 1
    // and subsequent rounds entering ClueGiving from RoleReveal etc.
    if (next.phase === GamePhase.ClueGiving && prev.phase !== GamePhase.ClueGiving) {
      if (this.isMyClueTurn(next)) this.player.play('yourClueTurn');
    }

    // Role-reveal trio: select by the eliminated role.
    if (next.phase === GamePhase.RoleReveal && prev.phase !== GamePhase.RoleReveal) {
      const role = next.roleRevealView?.eliminated?.role;
      if (role === 'villager') this.player.play('villagerEliminated');
      else if (role === 'ghost') this.player.play('ghostEliminated');
      else if (role === 'fool') this.player.play('foolEliminated');
    }
    // Village-win book-close → page-turn beat under the closing cinematic.
    // Ghost-win is a burn (not a page turn); stalemate is Lights-Out (no
    // cinematic). End-cadence loops themselves are reconciled below.
    if (next.phase === GamePhase.GameEnd && prev.phase !== GamePhase.GameEnd) {
      if (next.gameEndView?.side === 'village') this.player.play('villageWinFlip');
    }
    // End cadences are reconciled by `reconcileEndCadence` (called in
    // onSnapshot) — handles both edge entry into GameEnd and reconnect-into.
  }

  /** Starts the right end-cadence loop on entry to GameEnd, stops it on
   *  leave. Called on every snapshot — idempotent when the desired event
   *  matches what's already playing. The cadence registry entries are
   *  marked `loop: true` so once started they sustain until stopped. */
  private reconcileEndCadence(next: Snapshot): void {
    /* At Music volume 0 the player's play() is a silent no-op (no howl is
       created), so never latch a cadence that can't actually start — the
       0→up edge in `setVolume` re-runs this reconcile once playback is
       possible. An already-latched cadence is left alone: its howl exists
       and is merely volume-0, so a later slider raise resumes it in place
       rather than restarting the loop. */
    const desired =
      this.musicVolume === 0 && this.endCadencePlaying === null
        ? null
        : this.desiredEndCadence(next);
    if (desired === this.endCadencePlaying) return;
    if (this.endCadencePlaying !== null) this.player.stop(this.endCadencePlaying);
    if (desired !== null) this.player.play(desired);
    this.endCadencePlaying = desired;
  }

  private desiredEndCadence(next: Snapshot): AudioEvent | null {
    if (next.phase !== GamePhase.GameEnd) return null;
    const side = next.gameEndView?.side;
    if (side === 'village') return 'villageWin';
    if (side === 'ghosts') return 'ghostWin';
    if (side === 'none') return 'stalemate';
    return null;
  }
}
